import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import ItemService from "../../../services/commonService";
import ConstantsList from "../../../common/Constants";
import LoginByQRCode from "./LoginByQRCode";
import bgImage from "./login-background.png";
import "./Login.scss";

const Login = () => {
  const { t } = useTranslation();
  const deviceId = useSelector((state) => state?.user?.deviceId);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [headerName, setHeaderName] = useState("");
  const [headerLogo, setHeaderLogo] = useState("");
  const [forgotPassword, setForgotPassword] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    ItemService.getConfig().then((items) => {
      setHeaderName(items.PRODUCT_NAME);
      setHeaderLogo(items.PRODUCT_LOGO);
    });

    if (localStorage.getItem("token")) {
      window.location = "/home";
    }
  }, []);

  const handleLogin = (event, throughQRCode) => {
    event?.preventDefault();
    const toastId = toast.loading("Loading...", { position: "top-right" });

    if (!email || !password) {
      toast.error("Email and Password not be Empty !!", {
        duration: 3000,
        position: "top-right",
      });
      toast.dismiss(toastId);
      return;
    }

    ItemService.login({ email, password }).then((item) => {
      if (item?.status === 200) {
        toast.success(item?.message || "Login successful", {
          duration: 2000,
          position: "top-right",
        });
        window.location = ConstantsList.HOME;
      } else {
        toast.error(item?.message || "Invalid input", {
          duration: 4000,
          position: "top-right",
        });
        if (throughQRCode) {
          setEmail("");
          setPassword("");
        }
      }
      toast.dismiss(toastId);
    });
  };

  return (
    <div
      className="login-page"
      style={{
        backgroundImage: `linear-gradient(rgba(33, 78, 182, 0.44), rgba(33, 78, 182, 0.44)), url(${bgImage})`,
      }}
    >
      <div className="login-page__overlay" />
      <main
        className="login-page__main"
        style={{
          backgroundImage: `linear-gradient(rgba(33, 78, 182, 0.44), rgba(33, 78, 182, 0.44)), url(${bgImage})`,
        }}
      >
        <section className="login-page__content">
          {!forgotPassword ? (
            <div className="login-card">
              <div className="login-card__body">
                <div className="login-card__logo-wrap">
                  <img
                    src={
                      headerLogo ||
                      `${process.env.PUBLIC_URL}/assets/img/logo.png`
                    }
                    alt={headerName || "Yorsan Technologies"}
                    className="login-card__logo"
                  />
                </div>

                <div className="login-card__intro">
                  <div className="login-card__title">
                    {t("msg_login.msg_title1")}
                    <h6>{t("msg_login.msg_title2")}</h6>
                  </div>
                </div>

                <form className="login-card__form" onSubmit={handleLogin}>
                  <div className="login-card__field">
                    <label htmlFor="email">{t("msg_login.msg_username")}</label>
                    <div className="login-card__input-wrap">
                      <input
                        type="text"
                        name="email"
                        value={email}
                        id="email"
                        onChange={(event) => setEmail(event.target.value)}
                        autoComplete="username"
                        required
                      />
                    </div>
                  </div>

                  <div className="login-card__field">
                    <label htmlFor="password">
                      {t("msg_login.msg_password")}
                    </label>
                    <div className="login-card__input-wrap">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={password}
                        id="password"
                        onChange={(event) => setPassword(event.target.value)}
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        className="login-card__password-toggle"
                        onClick={() => setShowPassword((current) => !current)}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <path d="M2.25 12s3.5-6 9.75-6 9.75 6 9.75 6-3.5 6-9.75 6S2.25 12 2.25 12Z" />
                          <circle cx="12" cy="12" r="2.75" />
                          {!showPassword && (
                            <path
                              className="login-card__password-slash"
                              d="M4.25 4.25 19.75 19.75"
                            />
                          )}
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="login-card__actions">
                    <button
                      type="submit"
                      className="login-card__button login-card__button--primary"
                    >
                      <i className="bi bi-person-bounding-box" />
                      <span>Login</span>
                    </button>
                    <button
                      type="button"
                      className="login-card__button login-card__button--secondary"
                      onClick={() => setOpen(true)}
                    >
                      <i className="bi bi-qr-code-scan" />
                      <span>QR Code</span>
                    </button>
                  </div>
                </form>
              </div>
              <p className="login-card__device">
                <span className="login-card__device-badge">
                  <i className="bi bi-phone" aria-hidden="true" />
                  Device Code:{" "}
                  {localStorage.getItem("deviceCode") || "Not Configured"}
                </span>

                <a
                  className="login-card__device-badge login-card__manual"
                  href="/user-manual.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open User Manual in a new tab"
                >
                  <i className="bi bi-book" aria-hidden="true" />
                  User Manual
                </a>
              </p>
            </div>
          ) : (
            <div className="login-card login-card--reset">
              <div className="login-card__body">
                <h5 className="login-card__reset-title">Reset your password</h5>
                <p className="login-card__reset-copy">
                  Enter the email address associated with your account and we'll
                  send you a link to reset your password.
                </p>
                <form className="login-card__form">
                  <div className="login-card__field">
                    <label htmlFor="reset-email">Email</label>
                    <div className="login-card__input-wrap">
                      <input
                        type="email"
                        name="email"
                        value={email}
                        id="reset-email"
                        onChange={(event) => setEmail(event.target.value)}
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="login-card__button login-card__button--primary"
                  >
                    Continue
                  </button>
                  <button
                    type="button"
                    className="login-card__return"
                    onClick={() => setForgotPassword(false)}
                  >
                    Return to sign in
                  </button>
                </form>
              </div>
            </div>
          )}
        </section>

        {open && (
          <LoginByQRCode
            show={open}
            onHide={() => setOpen(false)}
            deviceId={deviceId}
          />
        )}
      </main>
    </div>
  );
};

export default Login;
